/**
 * The settings map (StreamRift mode, 2026-10-20): one entry per knob group at the top of Advanced, sticky, so any group
 * is one click away instead of a scroll through fourteen. Each entry carries how many of its knobs differ from the
 * trader's default and, while the search or the changed filter is on, how many it found ("found in"). Two densities:
 * chips where there is room (the Desk drawer, Saved Defaults) and one <select> where there is not (the 26 rem popover,
 * the ticket). Navigation only: it opens and scrolls, it never writes a setting.
 */
import { cn } from '@/lib/infra/utils';

export interface MapEntry {
  /** The DOM id the entry jumps to (groupDomId, or a tail block's fixed id). */
  id: string;
  /** The full group title (the tooltip; a tail entry's own name). */
  title: string;
  /** What the chip shows (shortGroupTitle). */
  short: string;
  /** Knobs in this group set differently from the default. */
  changed: number;
  /** Knobs the search / changed filter found in it; null when not filtering. */
  hits: number | null;
  open: boolean;
  /** A tail block (variants, the clipboard): jumped to, never remembered as the open group. */
  tail?: boolean;
}

/** The same chip classes as the editor's strategy bar. */
const chip = 'rounded border px-1.5 py-px font-mono text-[10px] disabled:opacity-50';

export function PenelopeSettingsMap({ entries, density, onJump, filtering }: {
  entries: MapEntry[];
  density: 'chips' | 'select';
  onJump: (entry: MapEntry) => void;
  filtering: boolean;
}) {
  const shown = filtering ? entries.filter((e) => e.hits !== 0) : entries;
  const opened = shown.filter((e) => e.open);
  const text = (e: MapEntry): string => `${e.short}${e.changed > 0 ? ` · ${e.changed} changed` : ''}${e.hits !== null ? ` (${e.hits})` : ''}`;
  return (
    <div role="group" aria-label="settings map" className="bg-background sticky top-0 z-10 flex flex-wrap items-center gap-1 py-1 text-[10px]">
      <span className="text-muted-foreground">{filtering ? 'found in' : 'jump to'}</span>
      {density === 'select' ? (
        <select
          aria-label="jump to a settings group"
          value={opened.length === 1 ? opened[0]!.id : ''}
          onChange={(e) => { const hit = shown.find((x) => x.id === e.target.value); if (hit) onJump(hit); }}
          className="border-border bg-background min-w-0 flex-1 rounded border px-1 py-0.5 font-mono"
        >
          <option value="">jump to…</option>
          {shown.map((e) => <option key={e.id} value={e.id}>{text(e)}</option>)}
        </select>
      ) : (
        shown.map((e) => (
          <button
            key={e.id}
            type="button"
            aria-pressed={e.open}
            data-group={e.title}
            title={`${e.title}${e.changed > 0 ? ` · ${e.changed} changed` : ''}`}
            onClick={() => onJump(e)}
            className={cn(chip, e.open ? 'border-sky-500 text-sky-500' : 'border-border/60 text-muted-foreground hover:text-foreground')}
          >
            {e.short}
            {e.changed > 0 && <span className="ml-0.5 text-sky-500">•{e.changed}</span>}
            {e.hits !== null && ` (${e.hits})`}
          </button>
        ))
      )}
    </div>
  );
}
